import { useEffect } from 'react';
import { SessionProvider, useSession } from './session.tsx';
import { RouterProvider, matchRoute, useRouter } from './router.tsx';
import { ToastProvider, Loading } from './ui.tsx';
import { LoginPage } from './pages/Login.tsx';
import { LibraryPage } from './pages/Library.tsx';
import { VideoEditorPage } from './pages/VideoEditor.tsx';
import { AnalyticsPage, LeadsPage } from './pages/Analytics.tsx';
import { DiagnosticsPage } from './pages/Diagnostics.tsx';
import { IntegrationsPage } from './pages/Integrations.tsx';
import { SettingsPage } from './pages/Settings.tsx';
import { InvitePage, ResetPasswordPage } from './pages/Recover.tsx';
import './styles.css';

const ICONS: Record<string, string> = {
  library: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  analytics: 'M4 19h16v2H4zM6 10h3v7H6zM11 5h3v12h-3zM16 12h3v5h-3z',
  leads: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 2c-4 0-8 2-8 5v1h16v-1c0-3-4-5-8-5z',
  diagnostics: 'M12 2 2 21h20L12 2zm0 6 6 11H6l6-11zm-1 4v3h2v-3h-2zm0 4v2h2v-2h-2z',
  integrations: 'M10 3v4H7l5 6 5-6h-3V3h-4zM4 17h16v4H4z',
  settings: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm9 4-2 1 .3 2.2-2 1.2-1.8-1.3-2 .8-.6 2.1h-2.3l-.6-2.1-2-.8-1.8 1.3-2-1.2L4.5 13 2.5 12l2-1-.3-2.2 2-1.2L6 8.9l2-.8.6-2.1h2.3l.6 2.1 2 .8 1.8-1.3 2 1.2L19 11l2 1z',
};

const NAV = [
  { path: '/app/videos', label: 'Biblioteca', icon: 'library' },
  { path: '/app/analytics', label: 'Analytics', icon: 'analytics' },
  { path: '/app/leads', label: 'Leads', icon: 'leads' },
  { path: '/app/diagnostics', label: 'Diagnóstico', icon: 'diagnostics' },
  { path: '/app/integrations', label: 'Integrações', icon: 'integrations' },
  { path: '/app/settings', label: 'Configurações', icon: 'settings' },
];

function NavIcon({ name }: { name: string }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d={ICONS[name] ?? ''} />
    </svg>
  );
}

function Shell(): JSX.Element {
  const { user, loading, workspace, workspaces, selectWorkspace, signOut, isDemo } = useSession();
  const { path, navigate } = useRouter();

  useEffect(() => {
    if (!loading && user && (path === '/' || path === '/login' || path === '/app')) {
      navigate('/app/videos', { replace: true });
    }
  }, [loading, user, path, navigate]);

  // Links de recuperação e convite funcionam antes de qualquer autenticação.
  if (path.startsWith('/redefinir-senha')) return <ResetPasswordPage />;
  if (path.startsWith('/convite')) return <InvitePage />;

  if (loading) {
    return <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}><Loading label="Carregando o Orbs…" /></div>;
  }

  if (!user) {
    return <LoginPage onAuthenticated={() => { window.location.href = '/app/videos'; }} />;
  }

  const videoMatch = matchRoute('/app/videos/:id', path);

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark" />
          <div>
            <div className="brand-name">Orbs</div>
            <div className="brand-sub">Vídeo interativo</div>
          </div>
        </div>

        <nav className="nav">
          {NAV.map((item) => (
            <button
              key={item.path}
              type="button"
              className="nav-item"
              aria-current={path.startsWith(item.path) ? 'page' : undefined}
              onClick={() => navigate(item.path)}
            >
              <NavIcon name={item.icon} />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="spacer" />

        <div className="sidebar-footer">
          <p className="nav-section" style={{ padding: '0 11px 6px' }}>Workspace</p>
          <select
            value={workspace?.id ?? ''}
            onChange={(event) => selectWorkspace(event.target.value)}
            aria-label="Workspace ativo"
          >
            {workspaces.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}{item.is_demo ? ' (demo)' : ''}
              </option>
            ))}
          </select>
          {isDemo && (
            <p className="card-sub" style={{ marginTop: 8 }}>
              Dados fictícios, somente leitura.
            </p>
          )}
          <button type="button" className="btn ghost small block" style={{ marginTop: 12 }} onClick={() => void signOut()}>
            Sair
          </button>
        </div>
      </aside>

      <main className="main">
        <div className="topbar">
          <strong style={{ fontSize: 13.5 }}>{workspace?.name ?? 'Sem workspace'}</strong>
          <span className="card-sub">{user.email}</span>
        </div>

        {videoMatch ? (
          <VideoEditorPage videoId={videoMatch.id!} />
        ) : path.startsWith('/app/analytics') ? (
          <AnalyticsPage />
        ) : path.startsWith('/app/leads') ? (
          <LeadsPage />
        ) : path.startsWith('/app/diagnostics') ? (
          <DiagnosticsPage />
        ) : path.startsWith('/app/integrations') ? (
          <IntegrationsPage />
        ) : path.startsWith('/app/settings') ? (
          <SettingsPage />
        ) : (
          <LibraryPage />
        )}
      </main>
    </div>
  );
}

export function App(): JSX.Element {
  return (
    <ToastProvider>
      <RouterProvider>
        <SessionProvider>
          <Shell />
        </SessionProvider>
      </RouterProvider>
    </ToastProvider>
  );
}
